define((require) => {
    "use strict";

    const _                                        = require("underscore");
    const ContentItemView                          = require("ui/dashboard/ContentItemView");
    const ContentQuery                             = require("data/ContentQuery");
    const CreateNewExperienceView                  = require("ui/dashboard/CreateNewExperienceView");
    const DashboardContentCollectionViewController = require("ui/dashboard/DashboardContentCollectionViewController");
    const DashboardSortingPanel                    = require("ui/dashboard/DashboardSortingPanel");
    const DashboardHeaderFactory                   = require("ui/dashboard/DashboardHeaderFactory");
    const EditorEvents                             = require("shared/services/EditorEvents");
    const ImprovedView                             = require("editor/zbone/ImprovedView");
    const InfiniteScrollingCollectionView          = require("ui/common/InfiniteScrollingCollectionView");
    const NoResultsItemView                        = require("zbone/NoResultsItemView");
    const NotificationBar                          = require("editor/ui/notificationbar/NotificationBar");
    const Privileges                               = require("shared/security/Permissions").Privileges;
    const QuickView                                = require("ui/experienceOverview/QuickView");
    const Lightbox                                 = require("shared/ui/common/Lightbox");
    const QuickViewAddExperienceView               = require("ui/experienceOverview/QuickViewAddExperienceView");
    const Syncer                                   = require("shared/services/sync/Syncer");
    const template                                 = require("ui/dashboard/DashboardContentCollectionView.tpl");

    /**
     *
     * @class DashboardContentCollectionView
     *
     */
    return ImprovedView.extend({

        className: "content-collection-view",

        template: template,

        controller: DashboardContentCollectionViewController,

        itemView: ContentItemView,

        itemViewOptions: null,

        createLabelOptions: function() {
            return {
                label: "Create<br> New Experience<br> &#43",
                onClick: (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    const lightboxOptions = {
                        disableElementPosition: true,
                        css: {
                            top: "25vh",
                            left: "15vw",
                            right: "15vw",
                            position: "absolute"
                        },
                        showSpinner: true
                    };

                    const eventProperties = {triggerOrigin: "DashboardContentCollectionView"};
                    EditorEvents.trigger(EditorEvents.constants.LIST_TEMPLATES, eventProperties);

                    const lightboxView = new QuickViewAddExperienceView();
                    new Lightbox(lightboxView, lightboxOptions).open();
                }
            };
        },

        _collectionView: null,

        _sortingPanel: null,

        _showCreateDisclaimer: true,

        viewScope: function() {
            return {
                openQuickView: function(model) {
                    this.trigger("quickView:open", model);
                },

                duplicate: function(model) {
                    this.trigger("duplicate", model);
                },

                delete: function(model) {
                    this.trigger("delete", model);
                }
            };
        },

        regions: {
            sortingPanelRegion: ".sorting-panel-region",
            content: ".content"
        },

        ui: {
            content: ".content"
        },

        initialize: function() {
            const notification = NotificationBar.list().find((notification) => notification.message.includes("This experience contains widgets which cannot be used in emails"));
            if (notification) {
                NotificationBar.close({id: notification.id});
            }

            this.collection = this.options.collection;
            this.itemViewOptions = this.options.itemViewOptions || this.itemViewOptions;
            this._showCreateDisclaimer = this.options.showCreateDisclaimer !== false && Privileges.CREATE.can();

            const dashboardHeaderFactoryOptions = _.pick(this.options, "users", "principal");
            this.dashboardHeaderFactory = new DashboardHeaderFactory(dashboardHeaderFactoryOptions);
        },

        /**
         * Exposes the event source (most likely a collection) associated with the number of
         * items represented by  *this* dashboard sub view. The dashboard will use this for updating
         * the counting labels, by listening for relevant events.
         *
         * @return {Collection} The main collection for this dashboard sub view.
         *
         * @see DashboardView#showTab
         */
        getCountableEventSource: function() {
            return this.collection;
        },

        /**
         * @override
         *
         * We don't need Marionette's serialization here.
         */
        serializeData: function() {
            return {};
        },

        onShow: function() {
            this._sortingPanel = new DashboardSortingPanel();
            this.sortingPanelRegion.show(this._sortingPanel);

            this._collectionView = new InfiniteScrollingCollectionView({
                itemView: this.itemView,
                itemViewOptions: this.itemViewOptions && this.itemViewOptions.bind(this),
                itemEvents: this._bindItemEvents(),
                collection: this.collection,
                horizontalBuffer: 220,
                scrollTarget: _.result(this.options, "scrollTarget")
            });

            this.listenTo(this.collection, {
                "sync:state": function() { // only used for fetch
                    this._sortingPanel.disable(!Syncer.State.complete(this.collection));
                },

                "remove": function() {
                    // Edge case: If we're in "search mode" and deletes the last experience/group, call
                    // refresh to update the view, eventually displaying "No content found" (since we're still in "search mode")...
                    if (this.collection.length() === 0 && this._sortingPanel.isSearching()) {
                        this.refresh();
                    }
                },

                "reset": function() {
                    this.dashboardHeaderFactory.reset();
                }
            });

            this.listenTo(this._sortingPanel, {
                "sort": function(sorting) {
                    this.collection.query(sorting.query);
                },

                "search": function(searchPhase, sorting) {
                    this.collection.query(ContentQuery.search(searchPhase, sorting.query));
                }
            });

            this._collectionView.onAddingView = this.onAddingView.bind(this);

            this._collectionView.onEmpty = function() {
                if (this._sortingPanel.isSearching()) {
                    return new NoResultsItemView({type: "content"});
                }
                // Empty items collection top align fix
                const header = this.dashboardHeaderFactory.create("Empty", 0, null);
                if (this._showCreateDisclaimer) {
                    return [header, this._createDisclaimer()];
                }

                return [header];
            }.bind(this);

            this.content.show(this._collectionView);
        },

        refresh: function(sorting) {
            this.dashboardHeaderFactory.reset();

            this._sortingPanel.setSorting(sorting || this.getSorting());
        },

        /**
         * Invoked from the dashboard view when the tab representing this view become active/deactive.
         *
         * @param  {Boolean} active The active state.
         */
        active: function(active) {
            // Delegate active state to the InfiniteScrollingCollectionView
            // instance - this will enable/disable the scroll listener.
            this._collectionView.active(active);

            if (active) {
                this.refresh();
            }
        },

        setSorting: function(sorting) {
            this._sortingPanel.setSorting(sorting);
        },

        getSorting: function() {
            return this._sortingPanel.getSorting();
        },

        openQuickView: function(options) {
            this._quickViewLightbox = new Lightbox(new QuickView(options), {css: {width: 540}}).open();
        },

        onAddingView: function(view, index) {
            if (this._collectionView.at(index)) {
                return;
            }

            const header = this.dashboardHeaderFactory.create(this._sortingPanel.getSorting(), index, view.model);

            if (index === 0) {
                // The create disclaimer might be undefined if _showCreateDisclaimer is set to false
                const createItem = this._createDisclaimer();

                return createItem ? [header, createItem] : header;
            }

            return header;
        },

        _createDisclaimer: function() {
            if (this._showCreateDisclaimer) {
                return new CreateNewExperienceView(this.createLabelOptions());
            }
            // undefined...
        },

        _bindItemEvents: function() {
            const self = this,
                itemEvents = {};

            if (itemEvents) {
                _.each(this.itemEvents, (callback, event) => {
                    itemEvents[event] = callback.bind(self);
                });
            }

            return itemEvents;
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/dashboard/DashboardContentCollectionView.js